
import React, { useState, useEffect } from 'react';
import { IconShield, IconMail, IconSearch, IconQuant } from '../components/Icons';
import { useAiQ } from '../state/AiQState';
import { draftMarginCall, analyzeMarginDrivers } from '../services/geminiService';
import toast from 'react-hot-toast';

interface CounterpartyStatus {
    name: string;
    exposure: number;
    collateralHeld: number;
    threshold: number;
    marginBalance: number; // Collateral - Exposure
    status: 'Safe' | 'Deficit' | 'Excess';
}

const CollateralView: React.FC = () => {
    const { trades, systemDate } = useAiQ();
    const [counterparties, setCounterparties] = useState<CounterpartyStatus[]>([]);
    const [selectedCpty, setSelectedCpty] = useState<CounterpartyStatus | null>(null);
    const [draftEmail, setDraftEmail] = useState('');
    const [analysis, setAnalysis] = useState('');
    const [isDrafting, setIsDrafting] = useState(false);

    // Calculate Live Exposure
    useEffect(() => {
        const exposureMap: Record<string, number> = {};
        
        // 1. Sum Valuation per Counterparty
        trades.forEach(trade => {
            const cpty = trade.counterparty || 'Unknown';
            if (!exposureMap[cpty]) exposureMap[cpty] = 0;
            exposureMap[cpty] += trade.valuation;
        });

        // 2. Create Counterparty Status objects
        const statusList: CounterpartyStatus[] = Object.keys(exposureMap).map(name => {
            const exposure = exposureMap[name];
            // Mock: Assume we hold collateral equal to 80% of exposure initially, or 100% if negative
            // In a real system, this comes from a database of collateral movements
            const collateralHeld = exposure > 0 ? exposure * 0.85 : 0; 
            const threshold = 500000; // Minimum Transfer Amount
            
            const marginBalance = collateralHeld - exposure;
            
            let status: CounterpartyStatus['status'] = 'Safe';
            if (marginBalance < -threshold) status = 'Deficit'; // We need cash
            if (marginBalance > threshold) status = 'Excess';   // We return cash

            return {
                name,
                exposure,
                collateralHeld,
                threshold,
                marginBalance,
                status
            };
        });

        setCounterparties(statusList);
    }, [trades]);

    const handleSelect = async (cpty: CounterpartyStatus) => {
        setSelectedCpty(cpty);
        setDraftEmail('');
        setAnalysis('');
        
        if (cpty.status === 'Deficit') {
            setIsDrafting(true);
            const callAmount = Math.abs(cpty.marginBalance);
            
            // 1. Draft Email
            const email = await draftMarginCall(cpty.name, callAmount, 'USD', systemDate);
            setDraftEmail(email);

            // 2. Analyze Drivers
            const recentTrades = trades.filter(t => t.counterparty === cpty.name);
            const insight = await analyzeMarginDrivers(cpty.name, recentTrades);
            setAnalysis(insight);
            
            setIsDrafting(false);
        }
    };

    const handleSendCall = () => {
        toast.success(`Margin Call sent to ${selectedCpty?.name} Operations.`);
        setDraftEmail('');
    };

    return (
        <div className="flex h-full bg-slate-100">
            {/* Left: Exposure Monitor */}
            <div className="flex-1 flex flex-col border-r border-slate-200 bg-white">
                <div className="p-6 border-b border-slate-100">
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-1">
                        <IconShield /> Collateral & Margin
                    </h2>
                    <p className="text-xs text-slate-500">Daily Credit Risk & CSA Management</p>
                </div>
                
                <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-slate-50 text-slate-500">
                            <tr>
                                <th className="px-4 py-3 font-medium rounded-l-lg">Counterparty</th>
                                <th className="px-4 py-3 font-medium text-right">Net Exposure</th>
                                <th className="px-4 py-3 font-medium text-right">Collateral Held</th>
                                <th className="px-4 py-3 font-medium text-right">Margin Balance</th>
                                <th className="px-4 py-3 font-medium rounded-r-lg">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {counterparties.map(cpty => (
                                <tr 
                                    key={cpty.name} 
                                    onClick={() => handleSelect(cpty)}
                                    className={`cursor-pointer transition-colors ${selectedCpty?.name === cpty.name ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                                >
                                    <td className="px-4 py-4 font-medium text-slate-800">{cpty.name}</td>
                                    <td className="px-4 py-4 text-right font-mono">${cpty.exposure.toLocaleString()}</td>
                                    <td className="px-4 py-4 text-right font-mono text-slate-500">${cpty.collateralHeld.toLocaleString()}</td>
                                    <td className={`px-4 py-4 text-right font-mono font-bold ${cpty.marginBalance < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                        {cpty.marginBalance < 0 ? '-' : '+'}${Math.abs(cpty.marginBalance).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-4">
                                        {cpty.status === 'Deficit' && <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold">CALL REQUIRED</span>}
                                        {cpty.status === 'Excess' && <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold">RETURN EXCESS</span>}
                                        {cpty.status === 'Safe' && <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-xs font-bold">BALANCED</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Right: Margin Call Station */}
            <div className="w-96 bg-slate-50 flex flex-col overflow-y-auto custom-scrollbar border-l border-slate-200">
                {!selectedCpty ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-10 text-center">
                        <div className="opacity-20 mb-4 scale-150"><IconShield /></div>
                        <p>Select a counterparty to view margin status and issue calls.</p>
                    </div>
                ) : (
                    <div className="p-6 space-y-6">
                        <div>
                            <h3 className="text-lg font-bold text-slate-800">{selectedCpty.name}</h3>
                            <p className="text-xs text-slate-500 uppercase tracking-wider font-bold">CSA Agreement: ISDA-2016-NY</p>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
                            <div className="flex justify-between mb-2 text-sm">
                                <span className="text-slate-500">Total Exposure</span>
                                <span className="font-medium">${selectedCpty.exposure.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between mb-4 text-sm">
                                <span className="text-slate-500">Collateral Posted</span>
                                <span className="font-medium">${selectedCpty.collateralHeld.toLocaleString()}</span>
                            </div>
                            <div className={`border-t border-slate-100 pt-3 flex justify-between items-center ${selectedCpty.status === 'Deficit' ? 'text-red-600' : 'text-emerald-600'}`}>
                                <span className="font-bold text-sm uppercase">{selectedCpty.status === 'Deficit' ? 'Call Amount' : 'Excess'}</span>
                                <span className="text-xl font-bold">${Math.abs(selectedCpty.marginBalance).toLocaleString()}</span>
                            </div>
                        </div>

                        {/* AI Action Area */}
                        {selectedCpty.status === 'Deficit' ? (
                            <div className="space-y-4">
                                {isDrafting ? (
                                    <div className="p-8 text-center">
                                        <span className="inline-block w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-2"></span>
                                        <p className="text-xs text-blue-600 font-bold">AiQ Drafting Legal Notice...</p>
                                    </div>
                                ) : draftEmail ? (
                                    <div className="animate-fade-in-up">
                                        {analysis && (
                                            <div className="bg-blue-50 p-3 rounded border border-blue-100 text-xs text-blue-800 mb-4 flex gap-2">
                                                <div className="shrink-0 pt-0.5"><IconQuant /></div>
                                                <p>{analysis}</p>
                                            </div>
                                        )}
                                        
                                        <div className="bg-white border border-slate-300 rounded-lg overflow-hidden shadow-sm mb-4">
                                            <div className="bg-slate-100 px-3 py-2 border-b border-slate-200 text-xs font-bold text-slate-600 flex items-center gap-2">
                                                <IconMail /> Draft Email Preview
                                            </div>
                                            <textarea 
                                                className="w-full h-40 p-3 text-xs font-mono text-slate-700 resize-none focus:outline-none"
                                                value={draftEmail}
                                                onChange={(e) => setDraftEmail(e.target.value)}
                                            />
                                        </div>
                                        
                                        <button 
                                            onClick={handleSendCall}
                                            className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded shadow-lg transition-transform hover:-translate-y-0.5"
                                        >
                                            Issue Margin Call
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        ) : (
                            <div className="bg-emerald-50 p-4 rounded border border-emerald-100 text-center">
                                <div className="text-emerald-600 font-bold mb-1">No Action Required</div>
                                <p className="text-xs text-emerald-800">Collateral is sufficient to cover current exposure.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CollateralView;
